import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from 'src/environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';
import { Vehicle } from 'src/app/main/vehicles/vehicles.component';
import { Router } from '@angular/router';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import * as FingerprintJS from '@fingerprintjs/fingerprintjs';
import * as CryptoJS from 'crypto-js';
import { allConstant } from 'src/app/_helpers/const';
import { JSEncrypt } from 'jsencrypt';
import { TranslateService } from '@ngx-translate/core';

@Injectable({ providedIn: 'root' })
export class SharedService {
  private publicKey: string = `-----BEGIN PUBLIC KEY-----
MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAqFk94rdfXR9NBkNh44v3
Jke5lYmd9Tdb3jsQduMhHFlTi2+qLjBnaiOzYQbDHnN8MKnDSI/HOewBfX0UOAhw
S5rYO/MfpFnehylkCW0fttD7rP/CM1ZD7sQt+Xs8KgDj/7uLMy1Bw1KhEHRdND44
mBf9LIubtbv8NY4LBPyJUrTbcXpNqFDicVMN81Mq1hPrq0udqoBInaEkI+beCVWN
dHKHqeybmlkvs23+zGktp+hN0hlmxug78co5yJB4MT9ErvJAqOy0Pz93hNYoI7Nj
hwPf9za0m5VhF3kytwwNF8xue3gDH0wDXxhlEu+wGakto6Z+TJ6siELI1atiqTuH
gwIDAQAB
-----END PUBLIC KEY-----`;
  private fpPromise: Promise<any> | undefined;
  private selectedVehicleSource = new BehaviorSubject<Vehicle>({ make: '', model: '', year: '', vin: '' });
  selectedVehicle$ = this.selectedVehicleSource.asObservable();
  private isShowheaderSubject = new BehaviorSubject<boolean>(false);
  isShowheader$ = this.isShowheaderSubject.asObservable();
  private hideFooterSubject = new BehaviorSubject<any>({});
  hideFooter$ = this.hideFooterSubject.asObservable();
  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  get isLoading$() { return this.isLoadingSubject.asObservable(); }
  private readonly idSubject = new BehaviorSubject<string | null>(null);
  readonly id$ = this.idSubject.asObservable();

  url: any = environment.URL;
  apiUrl: any = environment.API_URL;

  constructor(
    private http: HttpClient,
    private router: Router,
    private translate: TranslateService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    if (isPlatformBrowser(this.platformId)) {
      this.fpPromise = FingerprintJS.load();
    }
  }

  // ── State management ──────────────────────────────────────────────────────────

  setSelectedVehicle(vehicle: any) { this.selectedVehicleSource.next(vehicle); }
  setIsShowHeader(isShow: boolean) { this.isShowheaderSubject.next(isShow); }
  hideFooterElement(x: any) { this.hideFooterSubject.next(x); }
  show() { this.isLoadingSubject.next(true); }
  hide() { this.isLoadingSubject.next(false); }
  setId(id: string | null): void { this.idSubject.next(id); }
  navigateTohome() { this.router.navigate(['']); }

  // ── Device / fingerprint utilities ────────────────────────────────────────────

  async getFingerprint(): Promise<string> {
    if (!isPlatformBrowser(this.platformId) || !this.fpPromise) return '';
    const fp = await this.fpPromise;
    const result = await fp.get();
    return result.visitorId;
  }

  getDevicePlatform(): string {
    const ua = navigator.userAgent;
    if (/android/i.test(ua)) return 'Android';
    if (/iPhone|iPad|iPod/i.test(ua)) return 'iOS';
    if (/Windows NT/i.test(ua)) return 'Windows';
    if (/Macintosh|Mac OS X/i.test(ua)) return 'macOS';
    if (/Linux/i.test(ua)) return 'Linux';
    return 'Unknown';
  }

  // ── File validation ───────────────────────────────────────────────────────────

  private allowedExtensions = ['png', 'jpg', 'jpeg', 'pdf'];
  fileValidationCheck(file: any) {
    if (!file) return { status: false, message: 'No file provided.' };
    const ext = file?.name?.split('.').pop()?.toLowerCase() || '';
    if (!this.allowedExtensions.includes(ext)) {
      return { status: false, message: this.translate.instant('tooltips.message36') };
    }
    return { status: true, message: 'success.' };
  }

  resumeFileValidationCheck(file: File): { status: boolean; message: string } {
    if (!file) return { status: false, message: 'No file selected.' };
    if (file.type !== 'application/pdf') return { status: false, message: 'Only PDF files are allowed.' };
    if (file.size > 5 * 1024 * 1024) return { status: false, message: 'File size should not exceed 5MB.' };
    return { status: true, message: 'File is valid.' };
  }

  // ── URL / CDN utilities ───────────────────────────────────────────────────────

  cloudFrontUrl(size: any, url: any) {
    if (url?.includes('/assets/')) {
      return `${environment.CF_URL}${size}/assets/${url.split('/assets/').pop()}`;
    }
    return url;
  }

  cloudFrontPdfUrl(url: string) {
    if (environment.production) {
      const pdfPath = url.replace(/^https?:\/\/[^/]+\/?/, '');
      return `${environment.CF_PDF_URL || environment.CF_URL}${pdfPath}`;
    }
    return url;
  }

  objectToQueryString(obj: any) {
    return Object.keys(obj)
      .filter(k => obj[k] !== undefined && obj[k] !== null)
      .map(k => `${encodeURIComponent(k)}=${encodeURIComponent(obj[k])}`)
      .join('&');
  }

  getImage() { return 'assets/img/imageres.jpg'; }

  removeKeysDeep(obj: any, keysToRemove: string[]): any {
    const clone = JSON.parse(JSON.stringify(obj));
    for (const keyPath of keysToRemove) {
      const parts = keyPath.split('.');
      let target = clone;
      for (let i = 0; i < parts.length - 1; i++) {
        if (target[parts[i]] == null) { target = null; break; }
        target = target[parts[i]];
      }
      if (target && parts[parts.length - 1] in target) {
        delete target[parts[parts.length - 1]];
      }
    }
    return clone;
  }

  // ── Encryption ────────────────────────────────────────────────────────────────

  newEncryptJson(data: any) { return data; }

  encryptJson(jsonData: any): string {
    const jsonString = JSON.stringify(jsonData);
    return CryptoJS.AES.encrypt(jsonString, CryptoJS.enc.Utf8.parse(allConstant.secretKey), {
      iv: allConstant.iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7,
    }).toString();
  }

  // ── Speech ────────────────────────────────────────────────────────────────────

  speak(text: string): void { speechSynthesis.cancel(); speechSynthesis.speak(new SpeechSynthesisUtterance(text)); }
  stop(): void { speechSynthesis.cancel(); }

  // ── SSE / Event source ────────────────────────────────────────────────────────

  geteventurl(url: string) { return this.url + url; }

  getEventService(url: string) {
    fetchEventSource(this.url + url, {
      method: 'GET',
      headers: { Authorization: 'Basic ' + window.btoa('admin:admin123') },
      onmessage: (event) => {
        if (!event.data) return;
        const data = JSON.parse(event.data);
        if (data?.eventName === 'USER_LOGOUT' && data?.success) {
          localStorage.removeItem('currentUser');
          this.navigateTohome();
        }
      },
    });
  }

  // ── Server time ───────────────────────────────────────────────────────────────

  /** GET getdbtime/getCurrentTime — server time with optional timezone param */
  getTimeFromServer(timezone: string): Observable<any> {
    return this.http.get<any>(`${this.url}getdbtime/getCurrentTime`, { params: { timezone } });
  }

  // ── Legacy HTTP (kept for backward compat — use SharedApiService for new code) ─

  userVerifyWithSendOtp(data: any): Observable<any> {
    return this.http.post(this.url + 'user/send-otp', data);
  }

  subscriptionService(data: any) {
    return this.http.post(this.url + 'common/subscribe', data);
  }

  globalFileUpload(file: any) {
    const headers = new HttpHeaders({ 'X-Special-Param': 'delete' });
    return this.http.post(this.url + 'upload/global-uploader', file, { headers });
  }
}


